import {
  WBAlert,
  WBBox,
  WBFlex,
  WBForm,
  WBTypography,
} from '@admiin-com/ds-web';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SignatureForm } from './SignatureForm';
import PdfViewer from '../PdfViewer/PdfViewer';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { findFirstErrorField } from '../../helpers/string';
import { Bill } from '../PayBill/PayBillCreateForm';
import { DocumentCTAButtons } from './DocumentCTAButtons';
import LottieAlert from '../LottieAlert/LottieAlert';
import { useResetTaxValueFromScanning } from './hooks/useResetTaxValueFromScanning';
import { hasDocumentAnalysisFailed } from '../../helpers/tasks';

export const ESignatureConfirm = React.forwardRef(
  (
    {
      handleNext,
      handleDraft,
      handleSaveForReview,
      handleBackToEdit,
      taxScanned,
      taxScanning,
      scanningSkipped,
      onSkipScanning,
      nextButtonText,
      defaultConfirmationChecked,
      skipDocumentAnalysisReset,
    }: {
      handleBackToEdit?: () => void;
      handleDraft?: () => Promise<void>;
      handleSaveForReview?: () => Promise<void>;
      handleNext: (data: any) => Promise<void>;
      taxScanned?: Bill | null;
      taxScanning?: boolean;
      scanningSkipped?: boolean;
      onSkipScanning?: () => void;
      nextButtonText?: string;
      defaultConfirmationChecked?: boolean;
      skipDocumentAnalysisReset?: boolean;
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { handleSubmit, setFocus } = useFormContext();
    const documents = useWatch({ name: 'documents' });
    const documentUrl = documents?.[0]?.src;
    const annotations = useWatch({ name: 'annotations' });
    const [loading, setLoading] = useState(false);
    const onSubmit = async (data: any) => {
      try {
        setLoading(true);
        await handleNext(data);
      } finally {
        setLoading(false);
      }
    };
    const onError = (errorFields: any) => {
      console.log(errorFields);
      // Helper function to recursively find the first error field

      const firstErrorField = findFirstErrorField(errorFields);
      console.log(firstErrorField);
      if (firstErrorField) {
        setFocus(firstErrorField as any); // Focus the first error field
        const errorElement = document.getElementsByName(firstErrorField)?.[0];
        console.log(errorElement);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth' }); // Smoothly scroll to the field
        }
      }
    };
    useResetTaxValueFromScanning(
      taxScanned ?? null,
      taxScanning ?? false,
      scanningSkipped ?? false,
      skipDocumentAnalysisReset
    );
    const [stopLoading, setStopLoading] = useState(false);
    const hasFailed = hasDocumentAnalysisFailed(
      taxScanned?.documentAnalysis ?? undefined
    );
    const disabled =
      (!!taxScanning && !scanningSkipped && !stopLoading) ||
      (hasFailed && !stopLoading);
    return (
      <WBForm
        mt={0}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onError)();
        }}
      >
        <WBTypography variant="h3" sx={{ display: ['none', 'flex'] }} mb={0}>
          {t('confirmSend', { ns: 'taskbox' })}
        </WBTypography>
        <WBFlex mt={[0, 2]} gap={2} mb={5}>
          <Section sx={{ display: ['none', 'flex'] }}>
            <PdfViewer
              ref={ref}
              documentUrl={documentUrl}
              annotations={annotations}
            />
          </Section>
          <WBFlex flex={1} flexDirection={'column'}>
            {taxScanning && !stopLoading ? (
              <LottieAlert
                lottiePath="/lottie/document-scanning.lottie"
                loop={true}
                title={t('scanningInProgressTitle', { ns: 'taskbox' })}
                message={t('scanningInProgressTaxMessage', { ns: 'taskbox' })}
                isIn={true}
                onSecondaryClick={() => {
                  setStopLoading(true);
                  if (onSkipScanning) {
                    onSkipScanning();
                  }
                }}
                secondaryBtnText="Skip"
              />
            ) : hasFailed && !stopLoading ? (
              <LottieAlert
                lottiePath="/lottie/document-scanning.lottie"
                loop={false}
                title={t('scanningFailedTitle', { ns: 'taskbox' })}
                message={t('scanningFailedTaxMessage', { ns: 'taskbox' })}
                severity="warning"
                isIn={true}
                onSecondaryClick={() => {
                  setStopLoading(true);
                }}
                secondaryBtnText={t('close', { ns: 'common' })}
              />
            ) : scanningSkipped && stopLoading === false ? (
              <WBBox width="100%">
                <WBBox px={3}>
                  <WBAlert severity="warning">
                    {t('scanningSkippedDueToFileSize', { ns: 'taskbox' })}
                  </WBAlert>
                </WBBox>
              </WBBox>
            ) : null}
            {((!taxScanned && !taxScanning) ||
              scanningSkipped ||
              (taxScanned &&
                (taxScanned?.documentAnalysis?.status === 'SCANNED' ||
                  hasFailed))) && (
              <SignatureForm
                scannedTax={scanningSkipped ? null : taxScanned ?? null}
              />
            )}
          </WBFlex>
        </WBFlex>
        <DocumentCTAButtons
          handleNext={handleNext}
          handleDraft={handleDraft}
          handleSaveForReview={handleSaveForReview}
          handleBackToEdit={handleBackToEdit}
          loading={loading}
          disabled={disabled}
          nextButtonText={nextButtonText}
          defaultConfirmationChecked={defaultConfirmationChecked}
        />
      </WBForm>
    );
  }
);

const Section = styled(WBFlex)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  flex: 1,
  minHeight: '650px',
  maxHeight: '650px',
  marginBottom: theme.spacing(0),
  width: '50%',
}));
